// src/pages/TrashCustomersTable.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FiRotateCw, FiTrash2, FiEdit3 } from "react-icons/fi";
import Table from "../shared/table/Table";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import usePermission from "../../hooks/usePermission";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify"; 

const TrashCustomersTable = () => {
  /* ───────────── Hook'lar ───────────── */
  const lang = useUserLanguage();

  const { translations } = useTranslations(lang);
  const t = (k, d = "") => translations[k] || d;

  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const perm = usePermission("static_users");
  const canEdit = perm.canEdit || perm.canFull;
  const canRestore = perm.canEdit || perm.canFull;
  const canDeletePermanent = perm.canDelete || perm.canFull;

  /* ───────────── Çöp kutusu verisi çek ───────────── */
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("admin_token");
        const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/user/trash`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setItems(res.data || []);
      } catch (error) {
        console.error("Çöp kutusu verisi alınamadı:", error);
      }
    })();
  }, []);

  const handleRestore = (id) => {
    confirmAlert({
      title: t("RESTORE_CONFIRM_TITLE"),
      message: t("RESTORE_CONFIRM_MSG"),
      buttons: [
        {
          label: t("YES_RESTORE"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/user/restore/${id}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(t("SUCCESS_USER_RESTORED"));
              setItems(prev => prev.filter(item => item.id !== id));
            } catch (error) {
              console.error("Geri yükleme hatası:", error);
              toast.error(t("ERROR_USER_RESTORE_FAILED"));
            }
          },
        },
        {
          label: t("CANCEL"),
          onClick: () => toast.info(t("RESTORE_CANCELLED")),
        },
      ],
    });
  };

  const handlePermanentDelete = (id) => {
    confirmAlert({
      title: t("DESTROY_CONFIRM_TITLE"),
      message: t("DESTROY_CONFIRM_MSG"),
      buttons: [
        {
          label: t("YES_DESTROY"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/user/permanent-delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              toast.success(t("SUCCESS_USER_DESTROYED"));
              setItems(prev => prev.filter(item => item.id !== id));
            } catch (error) {
              console.error("Kalıcı silme hatası:", error);
              toast.error(t("ERROR_USER_DESTROY_FAILED"));
            }
          },
        },
        {
          label: t("CANCEL"),
          onClick: () => toast.info(t("DESTROY_CANCELLED")),
        },
      ],
    });
  };
  
  /* ───────────── Kolonlar ───────────── */
  const columns = useMemo(() => [
    { accessorKey: "id", header: t("TABLE_ID_LABEL"), size: 60 },

    {
      accessorKey: "profileImage",
      header: t("TABLE_PROFILE_IMAGE_LABEL"),
      enableSorting: false,
      size: 80,
      cell: (info) => {
        const imgPath = info.getValue();
        const src = imgPath
          ? `${process.env.REACT_APP_API_BASE_URL}${imgPath}`
          : `${process.env.REACT_APP_API_BASE_URL}/uploads/ProfileImages/default.svg`;
        return (
          <img
            src={src}
            alt="Profil"
            style={{ width: "45px", height: "45px", borderRadius: "50%", objectFit: "cover", border: "1px solid #ddd" }}
            onError={(e) => (e.currentTarget.src = `${process.env.REACT_APP_API_BASE_URL}/uploads/ProfileImages/default.svg`)}
          />
        );
      }
    },

    {
      accessorKey: "name",
      header: t("TABLE_USERS_LABEL"),
      size: 200,
      cell: (info) => {
        const firstName = info.row.original.firstName;
        const lastName = info.row.original.lastName;
        const fullName = `${firstName || ""} ${lastName || ""}`.trim() || "";
    
        return (
          <div className="hstack gap-2">
            <div>{fullName}</div>
          </div>
        );
      }
    },
    

    { accessorKey: "email", header: t("TABLE_EMAIL_LABEL"), size: 250 },
    { accessorKey: "phone", header: t("TABLE_PHONE_LABEL"), size: 150 },

    {
      accessorKey: "createdAt",
      header: t("TABLE_CREATED_AT"),
      meta: { headerClassName: "text-end", className: "text-end" },
      size: 140,
      cell: (info) => {
        const date = info.getValue();
        if (!date) return "-";
        return new Date(date).toLocaleDateString("tr-TR");
      }
    },

    {
      accessorKey: "deleted_at",
      header: t("TABLE_DELETED_AT"),
      meta: { headerClassName: "text-end", className: "text-end" },
      size: 140,
      cell: (info) => {
        const date = info.getValue();
        if (!date) return "-";
        return new Date(date).toLocaleDateString("tr-TR");
      }
    },

    {
      accessorKey: "actions",
      header: t("TABLE_ACTIONS_LABEL"),
      enableSorting: false,
      size: 180,
      meta: { headerClassName: "text-end", className: "text-end" },
      cell: (info) => {
        const id = info.row.original.id;
        return (
          <div className="d-flex gap-2 justify-content-end">
            
            {canRestore && (
              <button
                onClick={() => handleRestore(id)}
                className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                title={t("RESTORE")}
              >
                <FiRotateCw />
              </button>
            )}
            {canDeletePermanent && (
              <button
                onClick={() => handlePermanentDelete(id)}
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                title={t("DELETE_PERMANENT")}
              >
                <FiTrash2 />
              </button>
            )}
          </div>
        );
      }
    }
  ], [t, canEdit, canRestore, canDeletePermanent]);

  return <Table data={items} columns={columns} />;
};

export default TrashCustomersTable;
